import { FunctionComponent, Suspense, useEffect, useState } from 'react';
import { Navigation, TupasLogin, NavigationProps, Div } from '@gaiads/telia-react-component-library';
import { CartProps } from '@gaiads/telia-react-component-library/build/types/components/Navigation/Cart/Cart';
import { TFunction, useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router';
import { useRouteMatch } from 'react-router-dom';
import { UserRole } from '@cdaas/api-clients/dist/asset-user-openapi';

import './Header.scss';
import {
  checkIsSelectedOrganizationOwn,
  createAuthenticationUrl,
  getExternalLinkForMenuItem,
  getUserInitials,
  hasUserARequiredRole,
  isWebshopEnabled,
  setHeaderLanguage,
} from '../../../utils';
import { RootState } from '../../../store/reducers';
import * as actions from './../../../store/actions';
import { SupportedLanguage } from '../../../utils/constants';
import * as H from 'history';

type Language = {
  text: string;
  active: boolean;
  onClick: () => void;
};

type MenuItemArray = NavigationProps['menuItems'];

const Header: FunctionComponent = () => {
  const { t, i18n } = useTranslation('layoutComponents');
  const location = useLocation();
  const history = useHistory();
  const match = useRouteMatch({
    path: '/onboarding',
    strict: false,
    sensitive: false,
  });
  const loggedIn = useSelector((state: RootState) => state.authentication.authenticated);
  const usersName = useSelector((state: RootState) => state.authentication.user?.name);
  const product = useSelector((state: RootState) => state.shop.cart);
  const orderResponse = useSelector((state: RootState) => state.shop.orderResponse);
  const numberOfPendingApprovals = useSelector((state: RootState) => state.authentication.pendingApprovals);
  const ownOrganization = useSelector((state: RootState) => state.authentication.user?.organization);
  const selectedOrganization = useSelector((state: RootState) => state.authentication.selectedOrganization);
  const userOffboardingAt = useSelector((state: RootState) => state.authentication.user?.offboarding_at);

  const [languages, setLanguages] = useState<Language[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItemArray>([]);

  const dispatch = useDispatch();
  const logout = () => dispatch(actions.executeLogout());

  const selectedOrganizationIsOwn = checkIsSelectedOrganizationOwn(ownOrganization?.id, selectedOrganization?.id);
  const webshopEnabled = isWebshopEnabled(selectedOrganization, ownOrganization);

  const getHomeMenuItem = (loggedIn: boolean, t: TFunction) => (loggedIn ? [{ text: t('Header.menuItems.home'), url: '/home' }] : []);

  const getUserDevicesMenuItem = (t: TFunction, selectedOrganizationIsOwn: boolean) => {
    if (!hasUserARequiredRole([UserRole.User, UserRole.UserLite])) return [];
    const menuItems = [{ text: t('Header.menuItems.myOrders'), url: '/orders' }];
    if (selectedOrganizationIsOwn) {
      menuItems.unshift({
        text: t('Header.menuItems.myDevices'),
        url: '/myassets',
      });
    }
    return [
      {
        text: t('Header.menuItems.myDevices'),
        menuItems,
      },
    ];
  };

  const getWebshopMenuItem = (t: TFunction, selectedOrganizationIsOwn: boolean, webshopEnabled: boolean) => {
    const isUserOffboarded = userOffboardingAt != null && userOffboardingAt <= new Date().toISOString().slice(0, 10);
    if (isUserOffboarded) {
      return [];
    } else if (hasUserARequiredRole([UserRole.Orderer])) {
      const menuItems = [
        {
          text: t('Header.menuItems.orderForUser'),
          url: '/shop/loader/orderForUser',
        },
        {
          text: t('Header.menuItems.orderForOrganization'),
          url: webshopEnabled ? '/webshop?orderForOrganization=true' : '/shop/loader/orderForOrganization',
        },
      ];
      if (selectedOrganizationIsOwn) {
        menuItems.unshift({
          text: t('Header.menuItems.orderForMyself'),
          url: webshopEnabled ? '/webshop?ownOrder=true' : '/shop/loader/ownOrder',
        });
      }
      return [
        {
          text: t('Header.menuItems.shop'),
          menuItems,
        },
      ];
    } else if (hasUserARequiredRole([UserRole.User, UserRole.UserLite])) {
      return [
        {
          text: t('Header.menuItems.shop'),
          url: webshopEnabled ? '/webshop' : '/shop',
        },
      ];
    }
    return [];
  };

  const getChangePortalMenuItems = (t: TFunction, selectedOrganizationIsOwn: boolean, location: H.Location) => {
    if (!selectedOrganizationIsOwn || !hasUserARequiredRole([UserRole.Admin])) return [];
    return [
      {
        text: t('Header.menuItems.changePortal'),
        menuItems: [
          {
            text: t('Header.menuItems.minBedrift'),
            url: getExternalLinkForMenuItem(location.pathname, location.search, process.env.REACT_APP_MIN_BEDRIFT_3_URI ?? '', true),
          },
          {
            text: t('Header.menuItems.minBedriftHelp'),
            url: '/minbedrift/help',
          },
        ],
      },
    ];
  };

  const getAdminMenuItems = (t: TFunction) => {
    if (!hasUserARequiredRole([UserRole.Admin])) return [];
    return [
      {
        text: t('Header.menuItems.settingsAndOnboarding'),
        menuItems: [
          {
            text: t('Header.menuItems.companySettings'),
            url: '/organization',
          },
          {
            text: t('Header.menuItems.groups'),
            url: '/onboarding/usergroups',
          },
          {
            text: t('Header.menuItems.usersAndOnboarding'),
            url: '/users',
          },
        ],
      },
    ];
  };

  const getOrganizationOrdersMenuItem = (t: TFunction) => {
    if (!hasUserARequiredRole([UserRole.Admin, UserRole.Approver])) return [];
    const menuItems = [];
    if (hasUserARequiredRole([UserRole.Approver])) {
      menuItems.push({
        text: t('Header.menuItems.ordersForApproval'),
        url: '/approval',
      });
    }
    if (hasUserARequiredRole([UserRole.Admin])) {
      menuItems.push({
        text: t('Header.menuItems.orderOverview'),
        url: '/organizationorders',
      });
    }
    return [
      {
        text: t('Header.menuItems.organizationOrders'),
        menuItems,
      },
    ];
  };

  const getDevicesAndReportsMenuItem = (t: TFunction) =>
    hasUserARequiredRole([UserRole.Admin])
      ? [
          {
            text: t('Header.menuItems.devicesAndReports'),
            url: '/organizationassets',
          },
        ]
      : [];

  useEffect(() => {
    const newMenuItems: MenuItemArray = [
      ...getHomeMenuItem(loggedIn, t),
      ...getUserDevicesMenuItem(t, selectedOrganizationIsOwn),
      ...getWebshopMenuItem(t, selectedOrganizationIsOwn, webshopEnabled),
      ...getChangePortalMenuItems(t, selectedOrganizationIsOwn, location),
      ...getAdminMenuItems(t),
      ...getOrganizationOrdersMenuItem(t),
      ...getDevicesAndReportsMenuItem(t),
    ];
    setMenuItems(newMenuItems);
  }, [location, loggedIn, i18n.language, t, numberOfPendingApprovals, selectedOrganizationIsOwn, webshopEnabled]);

  useEffect(() => {
    const changeLanguage = (lng: SupportedLanguage): void => {
      i18n.changeLanguage(lng);
      setHeaderLanguage(lng);
      setLanguages((prevLanguages: Language[]) => {
        return prevLanguages.map((lang: Language) => {
          lang.active = lang.text.toLocaleLowerCase() === lng.toLocaleLowerCase();
          return lang;
        });
      });
    };
    const defaultLanguages: Language[] = [
      {
        text: 'EN',
        active: true,
        onClick: () => changeLanguage('en'),
      },
      {
        text: 'NO',
        active: false,
        onClick: () => changeLanguage('no'),
      },
    ];
    const selectedLang = i18n.language.toLocaleLowerCase().slice(0, 2);
    setHeaderLanguage(selectedLang);
    setLanguages(
      defaultLanguages.find(lang => lang.text.toLowerCase() === selectedLang)
        ? defaultLanguages.map((lang: Language) => {
            lang.active = lang.text.toLowerCase() === selectedLang;
            return lang;
          })
        : defaultLanguages,
    );
  }, [i18n]);

  const logo: NavigationProps['logo'] = {
    link: '/home',
    linkAriaLabel: 'Home',
  };

  const login: NavigationProps['login'] = {
    headingText: '',
    tabs: [
      {
        tabTitle: t('Header.loginMenu.login'),
        content: (
          <TupasLogin
            loginButtonLabel={t('Header.loginMenu.login')}
            onLoginButtonClick={() => {
              globalThis.location.href = createAuthenticationUrl(location.pathname);
            }}
          />
        ),
      },
    ],
    onLogout: () => {
      history.push('/authentication/logout');
      logout();
    },
  };

  let userMenu: NavigationProps['userMenu'] = false;

  if (loggedIn) {
    userMenu = {
      username: usersName || '',
      userMenuLinks: [],
      logoutButtonLabel: t('Header.logoutLink'),
      userInitials: getUserInitials(usersName ?? undefined),
    };
  }

  const showPendingApprovalsIcon = !!numberOfPendingApprovals && hasUserARequiredRole([UserRole.Approver]);

  const getCartProps = (): CartProps | undefined => {
    if (product && !orderResponse) {
      return { amount: 1, linkAriaLabel: 'Link to product cart', link: '/shop/checkout' };
    }
    return undefined;
  };

  return (
    <Suspense fallback="loading">
      <Div className={['navigation-wrapper', showPendingApprovalsIcon && 'pending-approvals']}>
        <Navigation
          cart={getCartProps()}
          logo={logo}
          languages={languages}
          login={login}
          menuItems={match?.path === '/onboarding' ? [] : menuItems}
          userMenu={userMenu}
        />
      </Div>
    </Suspense>
  );
};

export default Header;
